import { useCallback, useState } from 'react';

import { hashResource, RESOURCE_TYPES } from 'src/data/performance';
import { usePerformanceActions, useNotificationActions, useIsCurrentUserAdmin } from 'src/store';
import {
  AD_ACCOUNTS_SELECTION_POPUP,
  useAdAccountsSelectionPopup,
  useAddResourcePopup,
  useConnectToChannelPopup,
  usePopups,
} from './popup';
import { useRestrictActionPopup } from './useRestrictActionPopup';
import { useErrorHandler } from 'src/hooks';
import { CHANNEL } from 'src/data/channel';
import { useAddResource, useChannelAccounts } from 'src/services';

export const useConnectToChannel = (trackingEventAttributes = {}) => {
  const notifications = useNotificationActions();
  const performanceActions = usePerformanceActions();
  const openAdAccountsSelectionPopup = useAdAccountsSelectionPopup();
  const openAddResourcePopup = useAddResourcePopup();
  const handleError = useErrorHandler();
  const isCurrentUserAdmin = useIsCurrentUserAdmin();
  const openRestrictActionPopup = useRestrictActionPopup();
  const openConnectToChannelPopup = useConnectToChannelPopup();
  const onAddResource = useAddResource(trackingEventAttributes);
  const { connectedAccounts } = useChannelAccounts();

  const popups = usePopups();

  const [connecting, setConnecting] = useState();

  const addResource = useCallback(async () => {
    setConnecting(true);
    try {
      const result = await openAddResourcePopup();
      if (!result) {
        return;
      }

      return hashResource({
        channel: result.channel,
        id: result.id,
        name: result?.urls?.length ? result?.urls[0] : result.name,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setConnecting(false);
    }
  }, [openAddResourcePopup, handleError]);

  const connectToChannel = useCallback(
    async ({
      channel,
      brandVoiceId,
      customModel,
      isTrainingModel,
      onConnectSuccess,
      connected,
      isTalkingPointsView,
    }) => {
      const isHubspotChannel = channel === CHANNEL.hubspot;
      if (isHubspotChannel && !connected) {
        const selectedChannel = await openConnectToChannelPopup({
          displayIntegrations: false,
          displayResourcesTemplates: true,
          title: 'Email',
          subTitle: 'Select one of the options to import email data',
          isTrainingModel,
          brandVoiceId,
          onConnectSuccess,
          isTalkingPointsView,
        });
        if (selectedChannel) {
          if (RESOURCE_TYPES.includes(selectedChannel)) {
            await onAddResource();
            onConnectSuccess?.();
            return;
          }
        } else {
          return;
        }
      }

      if (
        performanceActions.isChannelConnected(channel) ||
        (isHubspotChannel && connected) ||
        ((isTrainingModel || isTalkingPointsView) && connectedAccounts[channel]?.length)
      ) {
        await openAdAccountsSelectionPopup({
          channel,
          brandVoiceId,
          customModel,
          isTrainingModel,
          onConnectSuccess,
          isTalkingPointsView,
        });
        return;
      }

      if (!isCurrentUserAdmin) {
        openRestrictActionPopup();
        return;
      }

      setConnecting(true);
      performanceActions
        .connectToChannel(channel)
        .catch((error) => {
          popups.closePopup(AD_ACCOUNTS_SELECTION_POPUP);
          notifications.displayError(error);
        })
        .finally(() => {
          setConnecting(false);
        });

      openAdAccountsSelectionPopup({
        channel,
        brandVoiceId,
        customModel,
        isTrainingModel,
        onConnectSuccess,
        isTalkingPointsView,
      });
    },
    [
      isCurrentUserAdmin,
      openRestrictActionPopup,
      openAdAccountsSelectionPopup,
      performanceActions,
      notifications,
      popups,
      openConnectToChannelPopup,
      onAddResource,
      connectedAccounts,
    ]
  );

  return {
    connectToChannel,
    connecting,
    addResource,
  };
};
